/**
 * useScoring Hook
 *
 * Fetches profiles list with server-side sorting and pagination.
 * Summary KPIs come from the /profiles/summary endpoint.
 * Income sources are fetched on-demand per client (useIncomeSources).
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  listProfiles,
  getProfilesSummary,
  getIncomeSources,
} from '@/services/credit-engine.service';
import { PARTNER_ID } from '@/constants';
import type { ProfilesListFilters, PaginationMeta, ProfileSummaryResponse } from '@/types/scoring.types';

const QUERY_KEYS = {
  profiles: (filters?: ProfilesListFilters) =>
    ['scoring', 'profiles', PARTNER_ID, filters ?? {}] as const,
  summary: () =>
    ['scoring', 'summary', PARTNER_ID] as const,
  incomeSources: (directoryId: string) =>
    ['scoring', 'income-sources', PARTNER_ID, directoryId] as const,
};

const DEFAULT_PAGE_SIZE = 50;

export function useScoring(filters?: ProfilesListFilters) {
  const params: ProfilesListFilters = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    sort_by: 'score',
    sort_order: 'desc',
    ...filters,
  };

  const skipProfiles = filters === undefined;

  const profilesQuery = useQuery({
    queryKey: QUERY_KEYS.profiles(params),
    queryFn: () => listProfiles(PARTNER_ID, params),
    enabled: !skipProfiles,
    // Mantiene la data previa visible mientras refetcha con un queryKey nuevo
    // (cada keystroke del buscador). Sin esto, la lista parpadea entre teclazos.
    placeholderData: keepPreviousData,
  });

  const summaryQuery = useQuery({
    queryKey: QUERY_KEYS.summary(),
    queryFn: () => getProfilesSummary(PARTNER_ID),
  });

  // Paginated shape: { items: [...], pagination: { page, page_size, total_items, total_pages } }
  const responseData = profilesQuery.data?.data;
  const profiles = responseData?.items ?? [];
  const pagination: PaginationMeta = responseData?.pagination ?? {
    page: params.page ?? 1,
    page_size: params.page_size ?? DEFAULT_PAGE_SIZE,
    total_items: 0,
    total_pages: 0,
  };

  const summary: ProfileSummaryResponse | null = summaryQuery.data?.data ?? null;

  return {
    profiles,
    pagination,
    summary,
    isSummaryLoading: summaryQuery.isLoading,
    isSummaryError: summaryQuery.isError,
    isLoading: profilesQuery.isLoading,
    isError: profilesQuery.isError,
    refetch: () => {
      profilesQuery.refetch();
      summaryQuery.refetch();
    },
  };
}

/**
 * Fetches income sources for a specific client.
 * Enabled only when directoryId is provided (on-demand).
 */
export function useIncomeSources(directoryId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.incomeSources(directoryId ?? ''),
    queryFn: () => getIncomeSources(directoryId!, PARTNER_ID),
    enabled: !!directoryId,
    select: (response) => {
      const raw = response.data;
      // API may return flat array or { directory_id, sources: [] }
      if (Array.isArray(raw)) {
        return { directory_id: directoryId!, sources: raw };
      }
      return raw;
    },
  });
}
